import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Save,
  Edit2,
  X,
} from "lucide-react";
import { db } from "../../firebase";
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import Toast from "../Toast/Toast";
import RichTextEditor from "../RichTextEditor/RichTextEditor";
import { useAuth } from "../../context/AuthContext";

const TradeTable = ({ trades, sortConfig, onSort, userSettings }) => {
  const { user } = useAuth();
  const [expandedRow, setExpandedRow] = useState(null);
  const [editingTrade, setEditingTrade] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [toast, setToast] = useState(null);
  const [newTag, setNewTag] = useState("");
  const [selectedTradeForTag, setSelectedTradeForTag] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [journalContent, setJournalContent] = useState({});
  const [loading, setLoading] = useState({});

  const tagColors = [
    "#10B981",
    "#3B82F6",
    "#F59E0B",
    "#EF4444",
    "#8B5CF6",
    "#EC4899",
    "#6366F1",
    "#F97316",
  ];

  // Add an effect to handle clicking outside of the color picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectedTradeForTag !== null &&
        !event.target.closest(".tag-color") &&
        !event.target.closest(".color-picker")
      ) {
        setSelectedTradeForTag(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedTradeForTag]);

  useEffect(() => {
    if (!user?.uid || !trades?.length) return;

    const fetchEntries = async () => {
      const newJournalContent = { ...journalContent };

      for (const trade of trades) {
        if (!trade.id || loading[trade.id]) continue;

        try {
          console.log(`Fetching journal entry for trade ${trade.id}`);
          const journalRef = doc(
            db,
            "journalEntries",
            `${user.uid}_${trade.id}`
          );
          const journalSnap = await getDoc(journalRef);

          if (journalSnap.exists()) {
            console.log("Found journal entry:", journalSnap.data());
            newJournalContent[trade.id] = journalSnap.data().content;
          }
        } catch (error) {
          console.error("Error fetching journal entry:", error);
        }
      }

      console.log("All journal content:", newJournalContent);
      setJournalContent(newJournalContent);
    };

    fetchEntries();
  }, [trades, user?.uid]);

  const handleDeleteTrade = async (tradeId) => {
    try {
      // Get the trade document first to verify ownership
      const tradeRef = doc(db, "trades", tradeId);
      const tradeDoc = await getDoc(tradeRef);

      if (!tradeDoc.exists()) {
        setToast({
          type: "error",
          message: "Error Deleting Trade",
          subText: "Trade not found",
        });
        return;
      }

      // Verify the trade belongs to the current user
      if (tradeDoc.data().userId !== user.uid) {
        setToast({
          type: "error",
          message: "Error Deleting Trade",
          subText: "You don't have permission to delete this trade",
        });
        return;
      }

      // Delete the trade
      await deleteDoc(tradeRef);

      // Try to delete journal entry if it exists
      try {
        const journalRef = doc(db, "journalEntries", `${user.uid}_${tradeId}`);
        const journalDoc = await getDoc(journalRef);
        if (journalDoc.exists()) {
          await deleteDoc(journalRef);
        }
      } catch (journalError) {
        console.log("No journal entry found for this trade");
        // Don't throw error if journal entry doesn't exist
      }

      // Trigger trades updated event
      window.dispatchEvent(new Event("tradesUpdated"));

      setToast({
        type: "success",
        message: "Trade Deleted",
        subText: "Trade successfully removed",
      });
    } catch (error) {
      console.error("Error deleting trade:", error);
      setToast({
        type: "error",
        message: "Error Deleting Trade",
        subText: error.message || "Please try again",
      });
    }
  };

  const calculateTradeResult = (trade) => {
    let percentReturn;

    // Check if it's an options trade by looking for P or C in the symbol
    const isOptions =
      trade.symbol &&
      (trade.symbol.includes("P") || trade.symbol.includes("C"));
    // Alternatively, check if there's a dedicated field
    const hasInstrumentType = trade.instrumentType !== undefined;
    const isOptionsType =
      hasInstrumentType && trade.instrumentType === "OPTIONS";

    // Use either method to determine if it's an options trade
    const isOptionTrade = isOptions || isOptionsType;

    if (isOptionTrade) {
      // For options, ALWAYS use standard calculation based on contract price change
      // regardless of direction (short/long)
      percentReturn =
        ((trade.exitPrice - trade.entryPrice) / trade.entryPrice) * 100;
    } else {
      // For futures, stocks, and other instruments, use direction-aware calculation
      if (trade.direction === "LONG") {
        percentReturn =
          ((trade.exitPrice - trade.entryPrice) / trade.entryPrice) * 100;
      } else {
        // Short trade calculation
        percentReturn =
          ((trade.entryPrice - trade.exitPrice) / trade.entryPrice) * 100;
      }
    }

    const threshold = userSettings?.customizeBreakeven
      ? userSettings.breakEvenThreshold
      : 2;

    if (Math.abs(percentReturn) <= threshold)
      return { percent: percentReturn, type: "breakeven" };
    return {
      percent: percentReturn,
      type: percentReturn > 0 ? "profit" : "loss",
    };
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);

  const formatPercentage = (value) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      signDisplay: "always",
    }).format(value) + "%";

  const calculateAdjustedPnL = (trade) => {
    if (!userSettings?.useCommissions) return trade.pnl;
    return trade.pnl - userSettings.commissionValue * trade.quantity * 2;
  };

  const calculateRRRatio = (reward, risk) => {
    if (!reward || !risk || risk === 0) return "";
    return `1:${(reward / risk).toFixed(2)}`;
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey)
      return <ChevronDown size={16} className="opacity-50" />;
    return sortConfig.direction === "asc" ? (
      <ChevronUp size={16} />
    ) : (
      <ChevronDown size={16} />
    );
  };

  const handleTagSubmit = (e) => {
    e.preventDefault();
    if (!newTag.trim()) return;

    const newTagObj = {
      text: newTag.trim(),
      color: tagColors[0],
    };

    setEditedValues((prev) => ({
      ...prev,
      tags: [...(prev.tags || []), newTagObj],
    }));
    setNewTag("");
  };

  const handleTagColorChange = (tagIndex, color) => {
    setEditedValues((prev) => {
      const updatedTags = [...prev.tags];
      updatedTags[tagIndex] = { ...updatedTags[tagIndex], color };
      return { ...prev, tags: updatedTags };
    });
  };

  const handleColorPickerOpen = (index, e) => {
    e.stopPropagation(); // Prevent other click handlers from firing
    setSelectedTradeForTag(index === selectedTradeForTag ? null : index);
  };

  const handleEditClick = (trade) => {
    setEditingTrade(trade.id);
    setEditedValues({
      risk: trade.risk || 0,
      reward: trade.reward || 0,
      setup: trade.setup || "",
      tags: trade.tags || [],
      journalContent: journalContent[trade.id] || "",
    });
  };

  const handleSaveClick = async (trade) => {
    try {
      if (!user?.uid) {
        throw new Error("User ID is required");
      }

      console.log("Saving journal content:", editedValues.journalContent);

      const tradeRef = doc(db, "trades", trade.id);
      const journalRef = doc(db, "journalEntries", `${user.uid}_${trade.id}`);

      const updates = {
        risk: parseFloat(editedValues.risk) || 0,
        reward: parseFloat(editedValues.reward) || 0,
        setup: editedValues.setup || "",
        tags: editedValues.tags || [],
      };

      await setDoc(tradeRef, updates, { merge: true });

      if (editedValues.journalContent !== undefined) {
        const journalData = {
          tradeId: trade.id,
          userId: user.uid,
          content: editedValues.journalContent || "",
          symbol: trade.symbol,
          tradeDate: trade.date,
          updatedAt: serverTimestamp(),
        };

        console.log("Saving journal data:", journalData);
        await setDoc(journalRef, journalData, { merge: true });

        // Verify save
        const verification = await getDoc(journalRef);
        console.log("Verification of saved data:", verification.data());

        setJournalContent((prev) => ({
          ...prev,
          [trade.id]: editedValues.journalContent,
        }));
      }

      setToast({ type: "success", message: "Trade Updated Successfully" });
      setEditingTrade(null);
      setEditedValues({});

      window.dispatchEvent(
        new CustomEvent("tradesUpdated", {
          detail: { tradeId: trade.id, updates },
        })
      );
    } catch (error) {
      console.error("Error updating trade:", error);
      console.error("Attempted content:", editedValues.journalContent);
      setToast({
        type: "error",
        message: "Error updating trade",
        subText: error.message || "Please try again",
      });
    }
  };

  const handleInputChange = (field, value) => {
    setEditedValues((prev) => ({ ...prev, [field]: value }));
  };

  const renderEditableField = (label, field, trade) => {
    const isEditing = editingTrade === trade.id;
    const value = isEditing ? editedValues[field] : trade[field];

    if (isEditing) {
      if (field === "tags") {
        return (
          <div className="tags-section">
            <div className="tags-container">
              {(editedValues.tags || []).map((tag, index) => (
                <div key={index} className="tag">
                  <span
                    className="tag-color"
                    style={{ backgroundColor: tag.color }}
                    onClick={(e) => handleColorPickerOpen(index, e)}
                  />
                  <span className="tag-text">{tag.text}</span>
                  <button
                    className="delete-tag"
                    onClick={() => {
                      const newTags = [...editedValues.tags];
                      newTags.splice(index, 1);
                      handleInputChange("tags", newTags);
                    }}
                  >
                    ×
                  </button>
                  {selectedTradeForTag === index && (
                    <div className="color-picker">
                      {tagColors.map((color) => (
                        <div
                          key={color}
                          className="color-option"
                          style={{ backgroundColor: color }}
                          onClick={() => {
                            handleTagColorChange(index, color);
                            setSelectedTradeForTag(null);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="tag-input-container">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleTagSubmit(e)}
                placeholder="Type and press Enter to add tag..."
                className="tag-input"
              />
            </div>
          </div>
        );
      }

      if (field === "journalContent") {
        console.log(
          "Rendering editor with content:",
          editedValues.journalContent
        );
        return (
          <RichTextEditor
            content={editedValues.journalContent || ""}
            onChange={(content) => handleInputChange("journalContent", content)}
          />
        );
      }

      return (
        <input
          type={field === "setup" ? "text" : "number"}
          value={value || ""}
          onChange={(e) => handleInputChange(field, e.target.value)}
          className="edit-input"
          step={field === "setup" ? undefined : "0.01"}
        />
      );
    }

    if (field === "tags") {
      return (
        <div className="tags-container">
          {(trade.tags || []).map((tag, index) => (
            <div key={index} className="tag">
              <span
                className="tag-color"
                style={{ backgroundColor: tag.color }}
              />
              <span className="tag-text">{tag.text}</span>
            </div>
          ))}
        </div>
      );
    }

    if (field === "journalContent") {
      console.log("Rendering saved content:", journalContent[trade.id]);
      return (
        <div
          className="journal-content-preview"
          dangerouslySetInnerHTML={{
            __html: journalContent[trade.id] || "No analysis added",
          }}
        />
      );
    }

    return (
      <span className="detail-value">
        {field === "risk" || field === "reward"
          ? formatCurrency(value || 0)
          : value || "N/A"}
      </span>
    );
  };

  return (
    <div className="trade-table-container">
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          subText={toast.subText}
          onClose={() => setToast(null)}
        />
      )}
      <table className="trade-table">
        <thead>
          <tr>
            <th></th>
            <th onClick={() => onSort("symbol")} className="sortable">
              <div className="th-content">
                <span>Symbol</span>
                {getSortIcon("symbol")}
              </div>
            </th>
            <th onClick={() => onSort("date")} className="sortable">
              <div className="th-content">
                <span>Date & Time</span>
                {getSortIcon("date")}
              </div>
            </th>
            <th onClick={() => onSort("direction")} className="sortable">
              <div className="th-content">
                <span>Side</span>
                {getSortIcon("direction")}
              </div>
            </th>
            <th onClick={() => onSort("entryPrice")} className="sortable">
              <div className="th-content">
                <span>Entry</span>
                {getSortIcon("entryPrice")}
              </div>
            </th>
            <th onClick={() => onSort("exitPrice")} className="sortable">
              <div className="th-content">
                <span>Exit</span>
                {getSortIcon("exitPrice")}
              </div>
            </th>
            <th onClick={() => onSort("quantity")} className="sortable">
              <div className="th-content">
                <span>Size</span>
                {getSortIcon("quantity")}
              </div>
            </th>
            <th onClick={() => onSort("pnl")} className="sortable">
              <div className="th-content">
                <span>P&L</span>
                {getSortIcon("pnl")}
              </div>
            </th>
            <th>%</th>
            <th></th> {/* New column for delete button */}
          </tr>
        </thead>
        <tbody>
          {trades.map((trade) => {
            const tradeResult = calculateTradeResult(trade);
            return (
              <React.Fragment key={trade.id}>
                <tr className={expandedRow === trade.id ? "expanded" : ""}>
                  <td>
                    <button
                      className="expand-button"
                      onClick={() =>
                        setExpandedRow(
                          expandedRow === trade.id ? null : trade.id
                        )
                      }
                    >
                      <ChevronRight
                        size={16}
                        className={expandedRow === trade.id ? "rotate-90" : ""}
                      />
                    </button>
                  </td>
                  <td>{trade.symbol}</td>
                  <td>{formatDate(trade.date)}</td>
                  <td>
                    <span
                      className={`direction-badge ${trade.direction.toLowerCase()}`}
                    >
                      {trade.direction}
                    </span>
                  </td>
                  <td>{formatCurrency(trade.entryPrice)}</td>
                  <td>{formatCurrency(trade.exitPrice)}</td>
                  <td>{trade.quantity}</td>
                  <td
                    className={
                      calculateAdjustedPnL(trade) >= 0 ? "profit" : "loss"
                    }
                  >
                    {formatCurrency(calculateAdjustedPnL(trade))}
                  </td>
                  <td className={tradeResult.type}>
                    {formatPercentage(tradeResult.percent)}
                  </td>
                  <td className="delete-trade-cell">
                    <button
                      className="delete-trade-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTrade(trade.id);
                      }}
                      title="Delete trade"
                    >
                      <X size={16} />
                    </button>
                  </td>
                </tr>
                {expandedRow === trade.id && (
                  <tr className="details-row">
                    <td colSpan="10">
                      <div className="trade-details">
                        <div className="trade-details-grid">
                          <div className="trade-detail">
                            <span className="detail-label">Setup</span>
                            {renderEditableField("Setup", "setup", trade)}
                          </div>
                          <div className="trade-detail">
                            <span className="detail-label">Risk</span>
                            {renderEditableField("Risk", "risk", trade)}
                          </div>
                          <div className="trade-detail">
                            <span className="detail-label">Reward</span>
                            {renderEditableField("Reward", "reward", trade)}
                          </div>
                          <div className="trade-detail">
                            <span className="detail-label">R:R Ratio</span>
                            <span className="detail-value">
                              {calculateRRRatio(
                                editingTrade === trade.id
                                  ? editedValues.reward
                                  : trade.reward,
                                editingTrade === trade.id
                                  ? editedValues.risk
                                  : trade.risk
                              )}
                            </span>
                          </div>
                          {userSettings?.useCommissions && (
                            <div className="trade-detail">
                              <span className="detail-label">Commissions</span>
                              <span className="detail-value">
                                {formatCurrency(
                                  userSettings.commissionValue *
                                    trade.quantity *
                                    2
                                )}
                              </span>
                            </div>
                          )}
                          <div className="trade-detail action-item">
                            {editingTrade === trade.id ? (
                              <button
                                className="save-button"
                                onClick={() => handleSaveClick(trade)}
                              >
                                <Save size={16} /> Save
                              </button>
                            ) : (
                              <button
                                className="edit-button"
                                onClick={() => handleEditClick(trade)}
                              >
                                <Edit2 size={16} /> Edit
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="trade-notes">
                          <span className="detail-label">Quick Tag</span>
                          {renderEditableField("Tags", "tags", trade)}
                        </div>
                        <div className="trade-notes">
                          <span className="detail-label">Analysis</span>
                          {renderEditableField(
                            "Analysis",
                            "journalContent",
                            trade
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TradeTable;
